// src/sim/ai/fear/accumulators.ts — P4 per-tick fear accumulator (D-17..D-22).
//
// Design invariants:
//   - Pure: returns { nextFear, boundaryEvents }; never mutates input.
//   - Entity.fear always clamped to [0, 1].
//   - Three thresholds (nervous/afraid/terrified) emit 'fear-threshold-crossed' on
//     boundary frames ONLY — strict-inequality per Pitfall P4-J.
//   - Fail-safe: unknown characterId → DEFAULT_CHARACTER profile, no throw.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-17..D-22; 04-RESEARCH.md Pattern 10.

import { lightLevelAt } from '../../lighting/light-level';
import type { Entity, WorldState } from '../../../shared/types/world';
import type { MapData } from '../../../shared/types/map';
import type { CharacterProfile } from '../../characters/character-profile';
import type { Perception } from '../../../shared/types/perception';
import {
  P4_FEAR_NERVOUS,
  P4_FEAR_AFRAID,
  P4_FEAR_TERRIFIED,
  P4_DARK_FEAR_RATE,
  P4_TORCH_OFF_FEAR_MULT,
  P4_ISOLATION_FEAR_RATE,
  P4_ISOLATION_NO_SIGHTING_WINDOW_TICKS,
  P4_ISOLATION_TEAMMATE_RADIUS_TILES,
  P4_FEAR_DECAY_RATE,
  P4_TRIGGER_FEAR_RATE,
  type FearThreshold,
} from './constants';

export interface ThresholdEvent {
  type: 'fear-threshold-crossed';
  entityId: string;
  threshold: FearThreshold;
  direction: 'up' | 'down';
  tick: number;
}

export interface FearAdvanceResult {
  nextFear: number;
  boundaryEvents: ThresholdEvent[];
}

/**
 * Advance fear for a single entity by one sim tick.
 *
 * Combines three accumulators:
 *   - Dark-fear:      (1 - lightLevel) * profile.fear.dark * P4_DARK_FEAR_RATE * (torchOn ? 1 : 2)
 *   - Isolation-fear: +alone*rate when no sighting for 150 ticks AND no teammate within 10 tiles;
 *                     −P4_FEAR_DECAY_RATE otherwise.
 *   - Trigger-fear:   +kind*rate for each fearTrigger whose radius covers entity (free hiders only).
 *
 * Threshold events fire on strict boundary crossings only (Pitfall P4-J).
 *
 * @param entity          — entity being updated
 * @param profile         — character profile (stats)
 * @param mapData         — static map data (lights, fearTriggers, tileWidth)
 * @param world           — current world state (entities for teammate scan, lightsState)
 * @param _perception     — unused in this accumulator; reserved for future extension
 * @param lastSightingTick — tick of most recent team sighting; null if never seen
 * @param currentTick     — current simulation tick
 */
export interface FearAdvanceOptions {
  /**
   * Ambient-light floor clamped into the dark-fear computation. Default 0
   * (pure formula). Production passes P4_AI_AMBIENT_LIGHT_FLOOR so hunters
   * do not peg to terrified across sparse-lit outdoor maps.
   */
  ambientLightFloor?: number;
  /**
   * When true, treat an entity as NOT isolated if no teammates of its role
   * exist in the world at all (semantically: isolation requires a team to
   * be separated from). Default false (existing behaviour — any lone entity
   * is isolated, which matches unit-test assumptions). Production passes
   * true so lone-hunter dev/UAT configurations do not lock into
   * retreat-when-afraid.
   */
  suppressIsolationWhenNoTeammatesExist?: boolean;
}

export function advanceFear(
  entity: Entity,
  profile: CharacterProfile,
  mapData: MapData,
  world: WorldState,
  _perception: Perception,
  lastSightingTick: number | null,
  currentTick: number,
  options?: FearAdvanceOptions,
): FearAdvanceResult {
  // Dark-fear (D-19, FEAR-03, FEAR-05)
  const rawLight = lightLevelAt(entity.position, mapData, world.lightsState, [], currentTick);
  // UAT Test 2 follow-up: opt-in ambient-light floor. Default 0 preserves
  // pure-formula behaviour for existing tests; production call sites pass
  // P4_AI_AMBIENT_LIGHT_FLOOR so the dark-fear ramp stops pegging to
  // terrified across ~95% of the sparse-authored map.
  const ambientFloor = options?.ambientLightFloor ?? 0;
  const light = Math.max(rawLight, ambientFloor);
  const torchMult = entity.torchOn ? 1 : P4_TORCH_OFF_FEAR_MULT;
  const darkDelta = (1 - light) * profile.fear.dark * P4_DARK_FEAR_RATE * torchMult;

  // Isolation-fear (D-20, FEAR-02)
  const noSighting =
    lastSightingTick === null ||
    (currentTick - lastSightingTick) > P4_ISOLATION_NO_SIGHTING_WINDOW_TICKS;
  const suppressNoTeammates = options?.suppressIsolationWhenNoTeammatesExist ?? false;
  const isolated =
    noSighting && noTeammateWithinRadius(entity, world, mapData, P4_ISOLATION_TEAMMATE_RADIUS_TILES, suppressNoTeammates);
  const iDelta = isolated
    ? profile.fear.alone * P4_ISOLATION_FEAR_RATE
    : -P4_FEAR_DECAY_RATE;

  // Trigger-fear (D-22, FEAR-06) — only applies to free hiders
  let tDelta = 0;
  if (entity.role === 'hider' && entity.status === 'free') {
    for (const trig of mapData.fearTriggers) {
      const dx = entity.position.x - trig.position.x;
      const dy = entity.position.y - trig.position.y;
      const d = Math.hypot(dx, dy);
      if (d <= trig.radiusTiles * mapData.tileWidth) {
        const kindStat = trig.kind === 'scorpion' ? profile.fear.scorpion : profile.fear.snake;
        tDelta += P4_TRIGGER_FEAR_RATE * kindStat;
      }
    }
  }

  const prev = entity.fear;
  const next = Math.max(0, Math.min(1, prev + darkDelta + iDelta + tDelta));

  // Threshold crossing emission — strict inequality (Pitfall P4-J)
  const boundaryEvents: ThresholdEvent[] = [];
  const thresholds: Array<{ name: FearThreshold; value: number }> = [
    { name: 'nervous',   value: P4_FEAR_NERVOUS },
    { name: 'afraid',    value: P4_FEAR_AFRAID },
    { name: 'terrified', value: P4_FEAR_TERRIFIED },
  ];
  for (const { name, value } of thresholds) {
    // Strict inequality — Pitfall P4-J:
    //   UP:   prev strictly below AND next at-or-above
    //   DOWN: prev at-or-above AND next strictly below
    //   Equal-to-threshold (prev=T, next=T) emits NOTHING.
    if (prev < value && next >= value) {
      boundaryEvents.push({
        type: 'fear-threshold-crossed',
        entityId: entity.id,
        threshold: name,
        direction: 'up',
        tick: currentTick,
      });
    } else if (prev >= value && next < value) {
      boundaryEvents.push({
        type: 'fear-threshold-crossed',
        entityId: entity.id,
        threshold: name,
        direction: 'down',
        tick: currentTick,
      });
    }
  }

  return { nextFear: next, boundaryEvents };
}

/**
 * Returns true if no teammate (same role, not caught/escorting/spectating)
 * is within `radiusTiles` of `self`.
 *
 * When `suppressWhenNoTeammates` is true and no same-role teammates exist in
 * the world at all, returns false — isolation semantically requires a team
 * to be separated from. Prevents lone-hunter dev/UAT configurations from
 * pegging isolation fear every tick. Pure-formula tests pass false (default)
 * to preserve the pre-fix behaviour.
 */
function noTeammateWithinRadius(
  self: Entity,
  world: WorldState,
  mapData: MapData,
  radiusTiles: number,
  suppressWhenNoTeammates: boolean,
): boolean {
  const radiusPx = radiusTiles * mapData.tileWidth;
  let teammateExists = false;
  for (const other of Object.values(world.entities)) {
    if (other.id === self.id) continue;
    if (other.role !== self.role) continue;
    if (
      other.status === 'caught' ||
      other.status === 'escorting' ||
      other.status === 'spectating'
    ) continue;
    teammateExists = true;
    const d = Math.hypot(
      other.position.x - self.position.x,
      other.position.y - self.position.y,
    );
    if (d <= radiusPx) return false;
  }
  if (!teammateExists && suppressWhenNoTeammates) return false;
  return true;
}
